/**
 * gateLibrary â€” templateâ†’gate-instance conversion + Gate Pack logic.
 * A Gate Pack = all active library templates sharing a workTypeKey
 * (in practice one TBT education + one PTW safety, optionally a QA/QC ride-along).
 */
import type { WorkLibraryItem } from '../../../hooks/useWorkLibrary';
import type {
  ActivityGates,
  CheckItem,
  EducationGate,
  EducationType,
  PermitType,
  QaqcBehavior,
  QaqcGate,
  SafetyGate,
  WorkTypeKey,
} from '../../../types';

// â”€â”€â”€ blank gates + converters (moved from planner/GateAuthoring.tsx) â”€â”€â”€â”€â”€â”€â”€â”€â”€

export function newCheckItem(label = ''): CheckItem {
  return { id: crypto.randomUUID(), label, required: true, photoRequired: false };
}

export function blankEducationGate(): EducationGate {
  return {
    gateId: crypto.randomUUID(),
    title: '',
    eduType: 'tbt',
    agendaTopics: [],
    briefing: '',
    checkItems: [],
    attachedByManager: true,
  };
}

export function blankSafetyGate(): SafetyGate {
  return {
    gateId: crypto.randomUUID(),
    title: '',
    permitType: 'other',
    hazards: [],
    ppe: [],
    precautions: [],
    revocable: false,
    attachedByManager: true,
  };
}

export function blankQaqcGate(): QaqcGate {
  return {
    gateId: crypto.randomUUID(),
    title: '',
    behavior: 'self',
    acceptanceCriteria: [],
    attachedByManager: true,
  };
}

export function educationGateFromLibrary(item: WorkLibraryItem): EducationGate {
  const p = item.payload ?? {};
  return {
    gateId: crypto.randomUUID(),
    sourceTemplateId: item.id,
    title: item.label,
    eduType: (p['eduType'] as EducationType) ?? 'tbt',
    agendaTopics: Array.isArray(p['agendaTopics']) ? (p['agendaTopics'] as string[]) : [],
    briefing: typeof p['briefing'] === 'string' ? p['briefing'] : '',
    checkItems: Array.isArray(p['checkItems'])
      ? (p['checkItems'] as CheckItem[]).map((c) => ({ ...c, id: crypto.randomUUID() }))
      : [],
    attachedByManager: true,
  };
}

export function safetyGateFromLibrary(item: WorkLibraryItem): SafetyGate {
  const p = item.payload ?? {};
  return {
    gateId: crypto.randomUUID(),
    sourceTemplateId: item.id,
    title: item.label,
    permitType: (p['permitType'] as PermitType) ?? 'other',
    hazards: Array.isArray(p['hazards'])
      ? (p['hazards'] as CheckItem[]).map((c) => ({ ...c, id: crypto.randomUUID() }))
      : [],
    ppe: Array.isArray(p['ppe'])
      ? (p['ppe'] as CheckItem[]).map((c) => ({ ...c, id: crypto.randomUUID() }))
      : [],
    precautions: Array.isArray(p['precautions'])
      ? (p['precautions'] as CheckItem[]).map((c) => ({ ...c, id: crypto.randomUUID() }))
      : [],
    revocable: typeof p['revocable'] === 'boolean' ? p['revocable'] : false,
    attachedByManager: true,
  };
}

export function qaqcGateFromLibrary(item: WorkLibraryItem): QaqcGate {
  const p = item.payload ?? {};
  return {
    gateId: crypto.randomUUID(),
    sourceTemplateId: item.id,
    title: item.label,
    behavior: (p['behavior'] as QaqcBehavior) ?? 'self',
    acceptanceCriteria: Array.isArray(p['acceptanceCriteria'])
      ? (p['acceptanceCriteria'] as CheckItem[]).map((c) => ({ ...c, id: crypto.randomUUID() }))
      : [],
    ...(typeof p['approverRoleId'] === 'string' && p['approverRoleId']
      ? { approverRoleId: p['approverRoleId'] }
      : {}),
    attachedByManager: true,
  };
}

// â”€â”€â”€ Gate Packs â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export const WORK_TYPE_LABELS: Record<WorkTypeKey, string> = {
  excavation: 'Excavation',
  electrical: 'Electrical',
  height: 'Height',
  lifting: 'Lifting',
  hotwork: 'Hot work',
  civil: 'Civil',
  other: 'Other',
};

export interface GatePack {
  workTypeKey: WorkTypeKey;
  label: string;
  education: WorkLibraryItem[];
  safety: WorkLibraryItem[];
  qaqc: WorkLibraryItem[];
}

export interface PackSourceLists {
  education: WorkLibraryItem[];
  safety: WorkLibraryItem[];
  qaqc: WorkLibraryItem[];
}

const PACK_ORDER: WorkTypeKey[] = ['excavation', 'electrical', 'height', 'lifting', 'hotwork', 'civil', 'other'];

/**
 * Groups library items into packs by workTypeKey. A pack only exists when it
 * has BOTH an education and a safety template (a chip that attaches half a
 * pre-start sequence would mislead the DM).
 */
export function buildGatePacks(lists: PackSourceLists): GatePack[] {
  const byKey = new Map<WorkTypeKey, GatePack>();
  const push = (kind: keyof PackSourceLists, item: WorkLibraryItem) => {
    const key = item.workTypeKey;
    if (!key) return;
    let pack = byKey.get(key);
    if (!pack) {
      pack = { workTypeKey: key, label: WORK_TYPE_LABELS[key], education: [], safety: [], qaqc: [] };
      byKey.set(key, pack);
    }
    pack[kind].push(item);
  };
  for (const i of lists.education) push('education', i);
  for (const i of lists.safety) push('safety', i);
  for (const i of lists.qaqc) push('qaqc', i);
  return PACK_ORDER.map((k) => byKey.get(k)).filter(
    (p): p is GatePack => !!p && p.education.length > 0 && p.safety.length > 0
  );
}

function packTemplateIds(pack: GatePack): Set<string> {
  return new Set([...pack.education, ...pack.safety, ...pack.qaqc].map((i) => i.id));
}

export type PackAttachment = 'none' | 'partial' | 'full';

/** How much of the pack is already attached (matched by sourceTemplateId). */
export function packAttachment(gates: ActivityGates, pack: GatePack): PackAttachment {
  const attached = new Set(
    [...gates.education, ...gates.safety, ...gates.qaqc]
      .map((g) => g.sourceTemplateId)
      .filter((id): id is string => !!id)
  );
  const ids = [...packTemplateIds(pack)];
  const hit = ids.filter((id) => attached.has(id)).length;
  if (hit === 0) return 'none';
  return hit === ids.length ? 'full' : 'partial';
}

/** Attach every pack template not already attached. Pure â€” returns new gates. */
export function applyPack(gates: ActivityGates, pack: GatePack): ActivityGates {
  const attached = new Set(
    [...gates.education, ...gates.safety, ...gates.qaqc]
      .map((g) => g.sourceTemplateId)
      .filter((id): id is string => !!id)
  );
  return {
    education: [
      ...gates.education,
      ...pack.education.filter((i) => !attached.has(i.id)).map(educationGateFromLibrary),
    ],
    safety: [
      ...gates.safety,
      ...pack.safety.filter((i) => !attached.has(i.id)).map(safetyGateFromLibrary),
    ],
    qaqc: [
      ...gates.qaqc,
      ...pack.qaqc.filter((i) => !attached.has(i.id)).map(qaqcGateFromLibrary),
    ],
  };
}

/** Remove every gate that came from this pack. Pure â€” returns new gates. */
export function removePack(gates: ActivityGates, pack: GatePack): ActivityGates {
  const ids = packTemplateIds(pack);
  const out = <G extends { sourceTemplateId?: string }>(arr: G[]) =>
    arr.filter((g) => !g.sourceTemplateId || !ids.has(g.sourceTemplateId));
  return { education: out(gates.education), qaqc: out(gates.qaqc), safety: out(gates.safety) };
}

/** Strips volatile ids + backend runtime fields so an instance can be compared against a fresh expansion. */
function stableGate(g: EducationGate | SafetyGate | QaqcGate): unknown {
  const strip = (items: CheckItem[]) => items.map(({ id: _id, ...rest }) => rest);
  if ('eduType' in g) {
    const { gateId: _g, checkItems, ...rest } = g;
    return { ...rest, checkItems: strip(checkItems) };
  }
  if ('permitType' in g) {
    const { gateId: _g, status: _s, hazards, ppe, precautions, ...rest } = g;
    return { ...rest, hazards: strip(hazards), ppe: strip(ppe), precautions: strip(precautions) };
  }
  const { gateId: _g, outcomeStatus: _o, firesBetween: _f, acceptanceCriteria, ...rest } = g;
  return { ...rest, acceptanceCriteria: strip(acceptanceCriteria) };
}

/** Key-order-insensitive JSON stringify (drops undefined-valued keys). */
function stableStringify(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(',')}]`;
  if (v && typeof v === 'object') {
    const entries = Object.entries(v as Record<string, unknown>)
      .filter(([, val]) => val !== undefined)
      .sort(([a], [b]) => a.localeCompare(b));
    return `{${entries.map(([k, val]) => `${JSON.stringify(k)}:${stableStringify(val)}`).join(',')}}`;
  }
  return JSON.stringify(v);
}

// ─── Individual library-item attach/detach (GateLibraryPicker) ──────────────

/** Template ids of every attached, template-sourced gate. */
export function attachedTemplateIds(gates: ActivityGates): Set<string> {
  return new Set(
    [...gates.education, ...gates.safety, ...gates.qaqc]
      .map((g) => g.sourceTemplateId)
      .filter((id): id is string => !!id)
  );
}

/** True when any attached gate came from this library item. */
export function isItemAttached(gates: ActivityGates, itemId: string): boolean {
  return attachedTemplateIds(gates).has(itemId);
}

/** Attach one library item as a fresh gate instance. No-op when already attached. Pure. */
export function attachLibraryItem(gates: ActivityGates, item: WorkLibraryItem): ActivityGates {
  if (isItemAttached(gates, item.id)) return gates;
  if (item.kind === 'education') {
    return { ...gates, education: [...gates.education, educationGateFromLibrary(item)] };
  }
  if (item.kind === 'safety') {
    return { ...gates, safety: [...gates.safety, safetyGateFromLibrary(item)] };
  }
  if (item.kind === 'qaqc') {
    return { ...gates, qaqc: [...gates.qaqc, qaqcGateFromLibrary(item)] };
  }
  return gates;
}

/** Detach every gate sourced from this library item; manual gates are untouched. Pure. */
export function detachLibraryItem(gates: ActivityGates, itemId: string): ActivityGates {
  const drop = <G extends { sourceTemplateId?: string }>(arr: G[]) =>
    arr.filter((g) => g.sourceTemplateId !== itemId);
  return { education: drop(gates.education), qaqc: drop(gates.qaqc), safety: drop(gates.safety) };
}

const EDU_TYPE_LABELS: Record<string, string> = {
  tbt: 'TBT',
  method_statement: 'Method statement',
  induction: 'Induction',
};

/** One-line "what is this" summary for a library gate template (picker rows). */
export function libraryItemSubtitle(item: WorkLibraryItem): string {
  const p = (item.payload ?? {}) as Record<string, unknown>;
  if (item.kind === 'education') {
    const t = EDU_TYPE_LABELS[String(p['eduType'] ?? 'tbt')] ?? 'TBT';
    const n = Array.isArray(p['checkItems']) ? p['checkItems'].length : 0;
    return n > 0 ? `${t} · ${n} check${n === 1 ? '' : 's'}` : t;
  }
  if (item.kind === 'safety') {
    const t = String(p['permitType'] ?? 'other');
    const n =
      (Array.isArray(p['hazards']) ? p['hazards'].length : 0) +
      (Array.isArray(p['ppe']) ? p['ppe'].length : 0) +
      (Array.isArray(p['precautions']) ? p['precautions'].length : 0);
    return n > 0 ? `${t} PTW · ${n} item${n === 1 ? '' : 's'}` : `${t} PTW`;
  }
  if (item.kind === 'qaqc') {
    const b = String(p['behavior'] ?? 'self');
    const n = Array.isArray(p['acceptanceCriteria']) ? p['acceptanceCriteria'].length : 0;
    return n > 0 ? `${b} · ${n} criteria` : b;
  }
  return '';
}

/** True when the DM has edited any attached gate away from its pack template. */
export function isPackEdited(gates: ActivityGates, pack: GatePack): boolean {
  const templateById = new Map([...pack.education, ...pack.safety, ...pack.qaqc].map((i) => [i.id, i]));
  const differs = (
    g: EducationGate | SafetyGate | QaqcGate,
    fresh: EducationGate | SafetyGate | QaqcGate
  ) => stableStringify(stableGate(g)) !== stableStringify(stableGate(fresh));
  for (const g of gates.education) {
    const t = g.sourceTemplateId ? templateById.get(g.sourceTemplateId) : undefined;
    if (t && differs(g, educationGateFromLibrary(t))) return true;
  }
  for (const g of gates.safety) {
    const t = g.sourceTemplateId ? templateById.get(g.sourceTemplateId) : undefined;
    if (t && differs(g, safetyGateFromLibrary(t))) return true;
  }
  for (const g of gates.qaqc) {
    const t = g.sourceTemplateId ? templateById.get(g.sourceTemplateId) : undefined;
    if (t && differs(g, qaqcGateFromLibrary(t))) return true;
  }
  return false;
}
